/**
 * أصوات التنبيهات: مُولَّدة عبر Web Audio (بلا ملفات صوت مضمّنة)،
 * مع إمكانية رفع صوت مخصص قصير لكل حدث. التفضيلات محلية للجهاز.
 */

import { useSyncExternalStore } from 'react';
import { getDeviceSetting, setDeviceSetting } from '@/services/settings-service';

export type SoundEvent = 'assignment' | 'completion' | 'alert';

export interface SoundPreferences {
  enabled: boolean;
  /** من 0 إلى 1 */
  volume: number;
  choices: Record<SoundEvent, string>;
  /** صوت مخصص (data URL) لكل حدث، إن وُجد يتقدّم على النغمة المختارة. */
  custom: Partial<Record<SoundEvent, string>>;
}

interface Tone {
  freq: number;
  /** بالثواني من بداية النغمة */
  start: number;
  duration: number;
  type?: OscillatorType;
  gain?: number;
}

export interface SoundPreset {
  id: string;
  label: string;
  tones: Tone[];
}

export const SOUND_PRESETS: SoundPreset[] = [
  {
    id: 'chime',
    label: 'رنّة مزدوجة',
    tones: [
      { freq: 880, start: 0, duration: 0.16, gain: 0.35 },
      { freq: 1318.5, start: 0.13, duration: 0.24, gain: 0.3 },
    ],
  },
  {
    id: 'bell',
    label: 'جرس هادئ',
    tones: [
      { freq: 1046.5, start: 0, duration: 0.5, gain: 0.28, type: 'sine' },
      { freq: 2093, start: 0, duration: 0.32, gain: 0.08, type: 'sine' },
    ],
  },
  {
    id: 'pop',
    label: 'نقرة',
    tones: [
      { freq: 420, start: 0, duration: 0.07, gain: 0.4, type: 'triangle' },
      { freq: 720, start: 0.05, duration: 0.1, gain: 0.3, type: 'triangle' },
    ],
  },
  {
    id: 'digital',
    label: 'رقمي',
    tones: [
      { freq: 1200, start: 0, duration: 0.07, gain: 0.28, type: 'square' },
      { freq: 1600, start: 0.09, duration: 0.09, gain: 0.24, type: 'square' },
    ],
  },
  {
    id: 'soft',
    label: 'ناعم',
    tones: [
      { freq: 660, start: 0, duration: 0.2, gain: 0.3, type: 'sine' },
      { freq: 880, start: 0.18, duration: 0.26, gain: 0.24, type: 'sine' },
    ],
  },
  {
    id: 'success',
    label: 'إنجاز',
    tones: [
      { freq: 523.25, start: 0, duration: 0.12, gain: 0.32, type: 'triangle' },
      { freq: 659.25, start: 0.1, duration: 0.12, gain: 0.32, type: 'triangle' },
      { freq: 783.99, start: 0.2, duration: 0.12, gain: 0.32, type: 'triangle' },
      { freq: 1046.5, start: 0.3, duration: 0.3, gain: 0.36, type: 'triangle' },
    ],
  },
  {
    id: 'alert',
    label: 'تنبيه',
    tones: [
      { freq: 740, start: 0, duration: 0.09, gain: 0.4, type: 'square' },
      { freq: 740, start: 0.16, duration: 0.09, gain: 0.4, type: 'square' },
      { freq: 740, start: 0.32, duration: 0.16, gain: 0.4, type: 'square' },
    ],
  },
  { id: 'none', label: 'صامت', tones: [] },
];

export const PRESET_IDS = new Set(SOUND_PRESETS.map((preset) => preset.id));

const DEFAULT_PREFERENCES: SoundPreferences = {
  enabled: true,
  volume: 0.7,
  choices: { assignment: 'chime', completion: 'success', alert: 'alert' },
  custom: {},
};

const STORAGE_KEY = 'sounds';

function sanitize(value: unknown): SoundPreferences {
  if (!value || typeof value !== 'object') return { ...DEFAULT_PREFERENCES };
  const raw = value as Partial<SoundPreferences>;
  const volume = typeof raw.volume === 'number' ? Math.min(1, Math.max(0, raw.volume)) : 0.7;
  const choices = { ...DEFAULT_PREFERENCES.choices };
  for (const event of ['assignment', 'completion', 'alert'] as SoundEvent[]) {
    const choice = raw.choices?.[event];
    if (typeof choice === 'string' && PRESET_IDS.has(choice)) choices[event] = choice;
  }
  const custom: Partial<Record<SoundEvent, string>> = {};
  for (const event of ['assignment', 'completion', 'alert'] as SoundEvent[]) {
    const dataUrl = raw.custom?.[event];
    if (typeof dataUrl === 'string' && dataUrl.startsWith('data:audio/')) custom[event] = dataUrl;
  }
  return { enabled: raw.enabled !== false, volume, choices, custom };
}

let current: SoundPreferences | null = null;
const listeners = new Set<() => void>();

export function getSoundPreferences(): SoundPreferences {
  if (!current) current = sanitize(getDeviceSetting<unknown>(STORAGE_KEY, null));
  return current;
}

export function saveSoundPreferences(next: Partial<SoundPreferences>): SoundPreferences {
  const merged = sanitize({ ...getSoundPreferences(), ...next });
  current = merged;
  setDeviceSetting(STORAGE_KEY, merged);
  for (const listener of listeners) listener();
  return merged;
}

export function resetSoundPreferences(): SoundPreferences {
  current = { ...DEFAULT_PREFERENCES, custom: {} };
  setDeviceSetting(STORAGE_KEY, current);
  for (const listener of listeners) listener();
  return current;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useSoundPreferences(): SoundPreferences {
  return useSyncExternalStore(subscribe, getSoundPreferences, getSoundPreferences);
}

/* --------------------------------- التشغيل -------------------------------- */

let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!audioContext) audioContext = new Ctor();
  if (audioContext.state === 'suspended') void audioContext.resume();
  return audioContext;
}

/** تُستدعى بعد أول تفاعل من المستخدم لفكّ حظر التشغيل التلقائي في المتصفح. */
export function unlockAudio(): void {
  getAudioContext();
}

export function playSound(event: SoundEvent, force = false): void {
  const preferences = getSoundPreferences();
  if (!force && !preferences.enabled) return;

  const custom = preferences.custom[event];
  if (custom) {
    try {
      const audio = new Audio(custom);
      audio.volume = preferences.volume;
      void audio.play().catch(() => undefined);
      return;
    } catch {
      // نكمل إلى النغمة المولّدة
    }
  }

  const preset = SOUND_PRESETS.find((entry) => entry.id === preferences.choices[event]);
  if (!preset || preset.tones.length === 0) return;
  const context = getAudioContext();
  if (!context) return;

  const now = context.currentTime + 0.01;
  for (const tone of preset.tones) {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = tone.type ?? 'sine';
    oscillator.frequency.setValueAtTime(tone.freq, now + tone.start);
    const peak = Math.max(0.0001, (tone.gain ?? 0.3) * preferences.volume);
    gain.gain.setValueAtTime(0.0001, now + tone.start);
    gain.gain.exponentialRampToValueAtTime(peak, now + tone.start + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + tone.start + tone.duration);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(now + tone.start);
    oscillator.stop(now + tone.start + tone.duration + 0.02);
  }
}

/** يعيد صوتًا مناسبًا لنوع الإشعار. */
export function soundForNotification(type: string): SoundEvent {
  switch (type) {
    case 'task.assigned':
    case 'task.awaiting_approval':
    case 'membership.added':
      return 'assignment';
    case 'task.completed':
    case 'task.approved':
      return 'completion';
    default:
      return 'alert';
  }
}
